/**
 * WidgetsTab — Settings → „Widgety" (Faza 23, T1.7).
 *
 * Lista definicji widgetów danych (katalog zapisany server-side, tenant-globalny — patrz
 * `useWidgetDefinitions.ts`) + kreator 6-krokowy (`WidgetCreatorWizard`). Mirror kształtu listy
 * z `RecordLinkTypesTab.tsx` (loading/empty/permission, tabela, ConfirmDialog przy kasowaniu),
 * ale akcje wierszowe przez `IconButton` (nie surowy `<button>` — RecordLinkTypesTab jest tu
 * starszym, zbaselinowanym wzorcem; nowy kod trzyma się bramki lintu z §4.12/README biblioteki UI).
 *
 * Jedno uprawnienie tenant-wide `crm_core.widgets.manage` (bez rozbicia view/manage — mirror
 * `AdditionalViewsTab.tsx`/`WidgetPickerModal.tsx`, zgodnie z docstringiem hooków).
 *
 * Konwencja UI (CLAUDE.md §4.12): ZERO window.alert/confirm/prompt — ConfirmDialog + useToast
 * (toasty CRUD są już wbudowane w `useWidgetDefinitionMutations`, tu dokładamy tylko ConfirmDialog).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { Spinner } from '@/components/Spinner';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useWidgetDefinitionMutations, useWidgetDefinitions } from '@/hooks/useWidgetDefinitions';
import { resolveIcon } from '@/lib/icon-map';
import type { WidgetDefinitionDTO } from '@/lib/widgets/types';

import { WidgetCreatorWizard } from './widget-creator/WidgetCreatorWizard';

const MANAGE_PERM = 'crm_core.widgets.manage';

export function WidgetsTab() {
  const { t } = useTranslation();
  const { me } = useTenantAuth();

  const isSuperUser = me?.user.isSuperUser === true;
  const canManage = isSuperUser || (me?.permissions.includes(MANAGE_PERM) ?? false);

  if (!canManage) {
    return (
      <Card padding="md" data-testid="widgets-tab-no-permission">
        <p className="text-sm text-text-tertiary">{t('tenant.widgets.list.noPermission')}</p>
      </Card>
    );
  }

  return <WidgetsList />;
}

function WidgetsList() {
  const { t } = useTranslation();
  const mutations = useWidgetDefinitionMutations();

  const [showDeleted, setShowDeleted] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<WidgetDefinitionDTO | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WidgetDefinitionDTO | null>(null);

  const listQ = useWidgetDefinitions(showDeleted);

  function openCreate() {
    setEditing(null);
    setWizardOpen(true);
  }

  function openEdit(row: WidgetDefinitionDTO) {
    setEditing(row);
    setWizardOpen(true);
  }

  function handleDelete(row: WidgetDefinitionDTO) {
    mutations.deleteDefinition.mutate({ id: row.id }, { onSuccess: () => setPendingDelete(null) });
  }

  if (listQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  const rows = listQ.data ?? [];

  return (
    <div data-testid="widgets-tab">
      <header className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.widgets.list.title')}
          </h2>
          <p className="text-xs text-text-tertiary">{t('tenant.widgets.list.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Checkbox
            label={t('tenant.widgets.list.showDeleted')}
            checked={showDeleted}
            onChange={(e) => setShowDeleted(e.target.checked)}
            data-testid="widgets-show-deleted"
          />
          <Button
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={openCreate}
            data-testid="widgets-new-btn"
          >
            {t('tenant.widgets.list.newButton')}
          </Button>
        </div>
      </header>

      {rows.length === 0 ? (
        <Card padding="md">
          <p className="text-sm text-text-tertiary">{t('tenant.widgets.list.empty')}</p>
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border-primary bg-bg-secondary text-xs uppercase tracking-wider text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('tenant.widgets.list.col.name')}</th>
                  <th className="px-4 py-2 text-left">{t('tenant.widgets.list.col.visualType')}</th>
                  <th className="px-4 py-2 text-left">{t('tenant.widgets.list.col.source')}</th>
                  <th className="px-4 py-2 text-left">{t('tenant.widgets.list.col.status')}</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {rows.map((row) => {
                  const IconCmp = resolveIcon(row.icon);
                  const isDeleted = row.gcrecord !== null;
                  return (
                    <tr key={row.id} data-testid={`widget-row-${row.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="rounded-lg bg-bg-tertiary p-1.5 text-text-tertiary">
                            <IconCmp className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <span className="font-medium text-text-primary">{row.name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        {t(`tenant.widgets.visualType.${row.visualType}`)}
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{row.source}</td>
                      <td className="px-4 py-3">
                        {isDeleted ? (
                          <Badge color="red">{t('tenant.widgets.list.deletedBadge')}</Badge>
                        ) : row.isActive ? (
                          <Badge color="green">{t('tenant.widgets.list.activeBadge')}</Badge>
                        ) : (
                          <Badge color="gray">{t('tenant.widgets.list.inactiveBadge')}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isDeleted ? (
                            <IconButton
                              size="sm"
                              aria-label={t('tenant.widgets.list.restoreAria')}
                              onClick={() => mutations.restoreDefinition.mutate(row.id)}
                              data-testid={`widget-restore-${row.id}`}
                            >
                              <RotateCcw aria-hidden="true" />
                            </IconButton>
                          ) : (
                            <>
                              <IconButton
                                size="sm"
                                aria-label={t('tenant.widgets.list.editAria')}
                                onClick={() => openEdit(row)}
                                data-testid={`widget-edit-${row.id}`}
                              >
                                <Pencil aria-hidden="true" />
                              </IconButton>
                              <IconButton
                                size="sm"
                                variant="danger"
                                aria-label={t('tenant.widgets.list.deleteAria')}
                                onClick={() => setPendingDelete(row)}
                                data-testid={`widget-delete-${row.id}`}
                              >
                                <Trash2 aria-hidden="true" />
                              </IconButton>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <WidgetCreatorWizard
        open={wizardOpen}
        editing={editing}
        onClose={() => {
          setWizardOpen(false);
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('tenant.widgets.confirmDelete.title')}
        message={
          pendingDelete
            ? t('tenant.widgets.confirmDelete.message', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        variant="danger"
        confirming={mutations.deleteDefinition.isPending}
        onConfirm={() => {
          if (pendingDelete) handleDelete(pendingDelete);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}
